import React, { useEffect, useMemo, useState } from 'react';

import EnhancedButton from '@components/button/EnhancedButton';
import TextEditorFormField from '@components/form-fields/TextEditorFormField';
import EnhancedModal from '@components/generic-modal/EnhancedModal';
import Loader from '@components/loader/Loader';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { sanitizeHtml } from '@utils/formatting-utils';

import { useGetAmendmentClauses } from '../amendment.slice';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  loaderContainer: {
    textAlign: 'center',
    padding: '40px 0'
  },
  loader: {
    width: 80,
    height: 80
  },
  section: {
    marginBottom: '24px'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '32px'
  }
}));

interface ConfirmAmendmentModalAtomProps {
  amendmentId: string;
  lineId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (clauseText?: string) => void | Promise<void>;
  defaultClauseText?: string;
  clauseRequired?: boolean;
}

interface ClauseOption {
  id: string;
  code: string;
  name: string;
  description: string;
}

const ConfirmAmendmentModalAtom: React.FC<ConfirmAmendmentModalAtomProps> = ({
  amendmentId,
  clauseRequired = true,
  lineId,
  open,
  onClose,
  onSuccess,
  defaultClauseText
}) => {
  const { classes } = useStyles();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clauses, setClauses] = useState<ClauseOption[]>([]);
  const [selectedClauseId, setSelectedClauseId] = useState<string>('');
  const [clauseText, setClauseText] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string>('');

  const { data: clausesData, loading: clausesLoading } = useGetAmendmentClauses(
    {
      variables: {
        lines: [lineId],
        pagination: { pageNumber: 1, pageSize: 1000 },
        statuses: ['Active']
      },
      skip: !lineId,
      fetchPolicy: 'no-cache'
    }
  );

  const convertNewlinesToBr = (text: string): string => {
    if (!text) return '';

    return text.replace(/\r\n|\r|\n/g, '<br>');
  };

  useEffect(() => {
    if (!open) return;

    setClauseText(
      defaultClauseText
        ? sanitizeHtml(convertNewlinesToBr(defaultClauseText))
        : ''
    );
    setTouched(false);
    setError('');
    setSelectedClauseId('');
    setSubmitting(false);
  }, [open, defaultClauseText]);

  useEffect(() => {
    if (clausesData?.planConfig?.getClauses?.items) {
      const items = clausesData.planConfig.getClauses.items;

      const clauseList: ClauseOption[] = items.map((item) => ({
        id: item.id,
        code: item.code?.toString() || '',
        name: item.name || '',
        description: item.description || ''
      }));

      setClauses(clauseList);

      setLoading(false);
    }
  }, [clausesData]);

  const clauseOptions = useMemo(() => {
    const result: Record<string, string> = {};
    clauses.forEach((c) => {
      result[c.id] = `${c.code} - ${c.name}`;
    });

    return result;
  }, [clauses]);

  const selectedClause = useMemo(() => {
    return clauses.find((c) => c.id === selectedClauseId) || null;
  }, [clauses, selectedClauseId]);

  const handleTextChange = (_: any, value: string | undefined | null) => {
    setClauseText(sanitizeHtml(value));
    setTouched(true);

    if (error) {
      setError('');
    }
  };

  const handleClauseSelect = (id: string) => {
    setSelectedClauseId(id);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const validateForm = (): boolean => {
    if (clauseRequired) {
      const sanitizedText = sanitizeHtml(clauseText);
      const textContent = sanitizedText.replace(/<[^>]*>/g, '').trim();

      if (!textContent || textContent.length === 0) {
        setError('Clause text is required');
        setTouched(true);

        return false;
      }

      setError('');
    }

    return true;
  };

  const handleConfirm = async () => {
    if (!validateForm() || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      await onSuccess(clauseText);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (loading || clausesLoading) {
    return (
      <EnhancedModal
        onClose={onClose}
        open={open}
        blur={true}
        title="Confirm Amendment"
      >
        <div className={classes.loaderContainer}>
          <Loader className={classes.loader} />
        </div>
      </EnhancedModal>
    );
  }

  return (
    <div>
      <WidgetSection title="Clause Details">
        <div className={classes.section}>
          <TextEditorFormField
            name="clauseText"
            title=""
            selectValue={selectedClauseId}
            placeholder="Enter clause text..."
            value={sanitizeHtml(clauseText)}
            onChange={handleTextChange}
            onBlur={handleBlur}
            error={clauseRequired && touched ? error : ''}
            htmlToAdd={sanitizeHtml(
              convertNewlinesToBr(selectedClause?.description)
            )}
            selectOptions={clauseOptions}
            onSelect={handleClauseSelect}
            addButton
          />
        </div>
      </WidgetSection>

      <div className={classes.buttonsContainer}>
        <EnhancedButton onClick={handleCancel} disabled={submitting}>
          Cancel
        </EnhancedButton>
        <EnhancedButton
          isPrimary
          state={submitting ? 'loading' : undefined}
          onClick={handleConfirm}
          disabled={submitting}
        >
          Confirm
        </EnhancedButton>
      </div>
    </div>
  );
};

export { ConfirmAmendmentModalAtom };
